
import {
  ComputedRef,
  defineComponent,
  onBeforeMount,
  onMounted,
  watch
} from "vue";
import { stringify } from "../../components/mixins";
import { useStore } from "vuex";
import { dispatchError } from "../../utils/error_handling";
import { Content, ScormCheckpoint } from "./helpers";
import { computed } from "@vue/reactivity";
import {
  ContentType,
  SCORMContent,
  SimpleFile
} from "../../../../domain/entities/content/content";
import { wrapLoading } from "../../utils/wrap_loading";

declare global {
  interface Window {
    API_1484_11: any;
    API: any;
  }
}

export default defineComponent({
  name: "SCORMViewer",
  setup() {
    const store = useStore();

    /** Scorm's boolean are represent as string. */
    type SCORMBoolean = "true" | "false";

    /** Basic variables to control the lifecycle of SCORM */
    let lastError = 0;

    onBeforeMount(async () => await onCurrentContentUpdate());

    const currentContent: ComputedRef<Content> = computed(
      () => store.getters["content_module/currentContent"]
    );
    const scorm: ComputedRef<SimpleFile> = computed(
      () => store.getters["content_module/scorm/scorm"]
    );
    const scormContent: ComputedRef<SCORMContent> = computed(
      () => store.getters["content_module/scorm/scormContent"]
    );

    watch(currentContent, async () => await onCurrentContentUpdate());

    const onCurrentContentUpdate = async (): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await stopProgressRoutine();
          if (currentContent.value.type !== ContentType.SCORM) return;
          await initializeModule();
          loadScormAPI();
          await startProgressRoutine();
        });
      } catch (e) {
        console.error("[onCurrentContentUpdate] Error ", e);
        await dispatchError(e);
      }
    };

    const stopProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/player/progress/stopProgressRoutine"
        );
      } catch (e) {
        console.error("[stopProgressRoutine] Error ", e);
        throw e;
      }
    };

    const initializeModule = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/scorm/initialize");
      } catch (e) {
        console.error("[stopProgressRoutine] Error ", e);
        throw e;
      }
    };

    const loadScormAPI = () => {
      if (
        scorm.value.scormVersion === "2004.2" ||
        scorm.value.scormVersion === "2004.3"
      ) {
        /** API Definition for SCORM 2004 */
        window.API_1484_11 = {
          Initialize: scormInitialize,
          Terminate: scormTerminate,
          GetValue: scormGetValue,
          SetValue: scormSetValue,
          Commit: scormCommit,
          GetLastError: scormGetLastError,
          GetErrorString: scormGetErrorString,
          GetDiagnostic: scormGetDiagnostic
        };
      } else {
        /** API Definition for SCORM 1.1 and 1.2 */
        window.API = {
          LMSInitialize: scormInitialize,
          LMSFinish: scormTerminate,
          LMSGetValue: scormGetValue,
          LMSSetValue: scormSetValue,
          LMSCommit: scormCommit,
          LMSGetLastError: scormGetLastError,
          LMSGetErrorString: scormGetErrorString,
          LMSGetDiagnostic: scormGetDiagnostic
        };
      }
    };

    const startProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch(
          "content_module/player/progress/startProgressRoutine"
        );
      } catch (e) {
        console.error("[startProgressRoutine] Error dispatch ", e);
        throw e;
      }
    };

    /** SPEC: Initialize(""): bool */
    function scormInitialize(data: string): SCORMBoolean {
      return "true";
    }

    /** SPEC: Terminate(""): string */
    function scormTerminate(data: string): SCORMBoolean {
      return "true";
    }

    /** SPEC: GetValue(CMIElement : string): string **/
    function scormGetValue(CMIElement): string {
      if (scormContent.value.has(CMIElement)) {
        return scormContent.value.get(CMIElement) as string;
      }
      return "";
    }

    /** SPEC: SetValue(CMIElement : string, value : string): string */
    function scormSetValue(CMIElement, value: string): string {
      const scormCheckpoint = new ScormCheckpoint(
        stringify(CMIElement),
        stringify(value)
      );
      store.dispatch("content_module/scorm/setValue", scormCheckpoint);
      return scormCheckpoint.value;
    }

    /** SPEC: Commit(""): SCORMBoolean */
    function scormCommit(data: string): SCORMBoolean {
      scormContent.value.forEach((value, key) => {
        if (typeof value === "string") {
          store.dispatch(
            "content_module/player/checkpoint/saveCheckpointSCORM",
            new ScormCheckpoint(key, value)
          );
        }
      });
      return "true";
    }

    /** SPEC: GetLastError(): CMIErrorCode */
    function scormGetLastError(): number {
      if (!lastError) return 0;
      console.warn("[scormGetLastError] Error ", lastError);
      return lastError;
    }

    /** SPEC: GetErrorString(CMIErrorCode: string) : string */
    function scormGetErrorString(CMIErrorCode: string): string {
      if (CMIErrorCode)
        console.error("[scormGetErrorString] Error ", CMIErrorCode);
      return CMIErrorCode;
    }

    /** SPEC: GetDiagnostic(errorCode: string) : string */
    function scormGetDiagnostic(errorCode): string {
      if (errorCode) console.error("[scormGetDiagnostic] Error ", errorCode);
      return errorCode;
    }

    return {
      scorm,
      scormContent
    };
  }
});
